<template lang="pug">
yz-dialog(v-model="showDialog" persistent scrollable max-width="500px")
  yz-card
    yz-dialog-normal-header(:title="dialogTitle" @close="onClose")

    yz-dialog-normal-content.pa-0
      .px-6.pt-4.pb-2
        .yz-regular-body-sm.text-neutral-500 {{ dialogSubtitle }}

      yz-tabs.px-6(v-model="activeTab" type="line")
        yz-tab(:label="isPreorder ? 'Pre-ordered' : 'Reserved'")
        yz-tab(label="Cancelled")

      .px-6.py-4
        template(v-if="isLoading")
          .d-flex.justify-center.py-8
            yz-progress-circular(indeterminate color="primary")

        template(v-else)
          template(v-if="activeTab === 0")
            yz-card.mb-3(v-for="(item, index) in reservedItems" :key="'reserved-' + index" outlined)
              .pa-4.clickable(@click="onSelectReservation(item)")
                .d-flex.align-center
                  .flex-grow-1
                    .yz-heading-9.text-neutral-700 {{ itemLabel }} \#{{ item.rid }}
                  yz-icon(size="20" color="neutral500") mdi-chevron-right
                template(v-for="product in getProducts(item)")
                  .d-flex.yz-regular-body-sm.text-neutral-700(:key="product.id || product.product_name" style="gap: 8px;")
                    span {{ product.name || product.product_name }}
                    span x{{ product.quantity }}

            .text-center.py-8(v-if="reservedItems.length === 0")
              .yz-regular-body-sm.text-neutral-500 {{ emptyActiveText }}

            .d-flex.justify-center.mt-2(v-if="reservedCount > reservedPageSize")
              yz-pagination(v-model="reservedPage" :pageSize="reservedPageSize" :count="reservedCount" @input="fetchReserved")

          template(v-if="activeTab === 1")
            yz-card.mb-3(v-for="(item, index) in cancelledItems" :key="'cancelled-' + index" outlined)
              .pa-4.clickable(@click="onSelectReservation(item)")
                .d-flex.align-center
                  .flex-grow-1
                    .yz-heading-9.text-neutral-700 {{ itemLabel }} \#{{ item.rid }}
                  yz-icon(size="20" color="neutral500") mdi-chevron-right
                template(v-for="product in getProducts(item)")
                  .d-flex.yz-regular-body-sm.text-neutral-700(:key="product.id || product.product_name" style="gap: 8px;")
                    span {{ product.name || product.product_name }}
                    span x{{ product.quantity }}

            .text-center.py-8(v-if="cancelledItems.length === 0")
              .yz-regular-body-sm.text-neutral-500 {{ emptyCancelledText }}

            .d-flex.justify-center.mt-2(v-if="cancelledCount > cancelledPageSize")
              yz-pagination(v-model="cancelledPage" :pageSize="cancelledPageSize" :count="cancelledCount" @input="fetchCancelled")


</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    open: { type: Boolean, default: false },
    type: { type: String, default: 'reservation' },
    patient: { type: Object, default: null },
    medication: { type: Object, default: null }
  },
  data: () => ({
    activeTab: 0,
    reservedItems: [],
    cancelledItems: [],
    reservedPage: 1,
    cancelledPage: 1,
    reservedPageSize: 10,
    cancelledPageSize: 10,
    reservedCount: 0,
    cancelledCount: 0,
    isLoading: false
  }),
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    showDialog: {
      get () { return this.open },
      set (val) { this.$emit('update:open', val) }
    },
    productId () {
      return this.medication?.product?.id || null
    },
    isPreorder () {
      return this.type === 'preorder'
    },
    dialogTitle () {
      return this.isPreorder ? 'View pre-order' : 'View reservation'
    },
    dialogSubtitle () {
      return this.isPreorder ? 'Which pre-order would you like to view?' : 'Which reservation would you like to view?'
    },
    itemLabel () {
      return this.isPreorder ? 'Pre-order' : 'Reservation'
    },
    emptyActiveText () {
      return this.isPreorder ? 'No active pre-orders' : 'No active reservations'
    },
    emptyCancelledText () {
      return this.isPreorder ? 'No cancelled pre-orders' : 'No cancelled reservations'
    },
    apiEndpoint () {
      return this.isPreorder ? `/clinics/${this.company.slug}/preorders` : `/clinics/${this.company.slug}/reservations`
    },
    viewRouteName () {
      return this.isPreorder ? 'ClinicPreorderView' : 'ClinicReservationView'
    },
    viewRouteParam () {
      return this.isPreorder ? 'preorderId' : 'reservationId'
    }
  },
  watch: {
    activeTab () {
      if (this.activeTab === 0 && this.reservedItems.length === 0) {
        this.fetchReserved()
      } else if (this.activeTab === 1 && this.cancelledItems.length === 0) {
        this.fetchCancelled()
      }
    }
  },
  mounted () {
    this.fetchReserved()
    this.fetchCancelled()
  },
  methods: {
    ...mapActions(['showMessage']),
    onClose () {
      this.showDialog = false
    },
    getBaseParams () {
      const params = {
        page_size: this.reservedPageSize
      }
      if (this.patient?.id) {
        params.patient = this.patient.id
      }
      if (this.productId) {
        params.medications = this.productId
      }
      return params
    },
    fetchReserved () {
      this.isLoading = true
      const params = { ...this.getBaseParams(), status: 'active', page: this.reservedPage, page_size: this.reservedPageSize }
      this.$axios.get(this.apiEndpoint, { params })
        .then(resp => {
          this.reservedItems = (resp.data.results || []).map(this.mapReservation)
          this.reservedCount = resp.data.count || 0
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    fetchCancelled () {
      this.isLoading = true
      const params = { ...this.getBaseParams(), status: 'cancelled', page: this.cancelledPage, page_size: this.cancelledPageSize }
      this.$axios.get(this.apiEndpoint, { params })
        .then(resp => {
          this.cancelledItems = (resp.data.results || []).map(this.mapReservation)
          this.cancelledCount = resp.data.count || 0
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    getProducts (item) {
      return item.items || item.products || []
    },
    mapReservation (r) {
      return {
        id: r.id,
        name: r.patient?.name || '-',
        ic: r.patient?.ic || '-',
        rid: r.rid,
        items: r.items || r.products || []
      }
    },
    onSelectReservation (item) {
      const route = this.$router.resolve({ name: this.viewRouteName, params: { [this.viewRouteParam]: String(item.id) } })
      window.open(route.href, '_blank')
    }
  }
}
</script>
